import { useEffect, useState, useCallback, useMemo } from 'react';
import type { AcceptRefinementResponse, ImageRefDTO } from '@historyjki/shared';
import { api, toastError } from '../utils/api';
import { useToast } from './useToast';
import {
  emptySession,
  type RefineStep,
  type RefinementSession,
} from '../components/stages/refine/types';

export const STEP_ORDER: RefineStep[] = [
  'intent',
  'describing',
  'review',
  'references',
  'generating',
  'compare',
];

export const STEP_LABELS: Record<RefineStep, string> = {
  intent: 'Intent',
  describing: 'Analyze',
  review: 'Review',
  references: 'References',
  generating: 'Generate',
  compare: 'Compare',
};

function isValidJson(raw: string): boolean {
  try {
    JSON.parse(raw);
    return true;
  } catch {
    return false;
  }
}

interface UseRefineImageArgs {
  open: boolean;
  imageRef: ImageRefDTO;
  currentR2Key: string;
  onClose: () => void;
  onAccepted: (result: AcceptRefinementResponse) => void;
}

/**
 * The refine-image wizard's business logic: the step state machine, session
 * draft, candidate invalidation, and the accept/reject/close API calls. The
 * modal component owns only the stepper chrome and per-step rendering.
 */
export function useRefineImage({
  open,
  imageRef,
  currentR2Key,
  onClose,
  onAccepted,
}: UseRefineImageArgs) {
  const { showToast } = useToast();
  const [step, setStep] = useState<RefineStep>('intent');
  const [session, setSession] = useState<RefinementSession>(emptySession);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const canProceedIntent = session.intent.trim().length >= 3;
  const canProceedReview = useMemo(
    () => isValidJson(session.editedDescription ?? ''),
    [session.editedDescription],
  );

  useEffect(() => {
    if (!open) return;
    setStep('intent');
    setSession(emptySession());
    setAccepting(false);
    setRejecting(false);
  }, [open, currentR2Key]);

  const rejectCandidateIfAny = useCallback(
    (candidateR2Key: string | null) => {
      if (!candidateR2Key) return;
      api
        .post(`/projects/${imageRef.project_id}/refine/reject-refinement`, {
          image_ref: imageRef,
          candidate_r2_key: candidateR2Key,
        })
        .catch(() => {});
    },
    [imageRef],
  );

  const handleClose = useCallback(() => {
    rejectCandidateIfAny(session.candidateR2Key);
    onClose();
  }, [rejectCandidateIfAny, session.candidateR2Key, onClose]);

  const invalidateFrom = useCallback(
    (from: RefineStep) => {
      setSession((prev) => {
        const next: RefinementSession = { ...prev };
        const fromIdx = STEP_ORDER.indexOf(from);
        if (fromIdx <= STEP_ORDER.indexOf('describing')) {
          next.currentDescription = null;
          next.aiUpdatedDescription = null;
          next.editedDescription = null;
        }
        if (fromIdx <= STEP_ORDER.indexOf('generating')) {
          if (prev.candidateR2Key) rejectCandidateIfAny(prev.candidateR2Key);
          next.candidateR2Key = null;
          next.candidateImageUrl = null;
        }
        return next;
      });
    },
    [rejectCandidateIfAny],
  );

  // ─── Step handlers ────────────────────────────────────────────────

  const handleIntentNext = () => {
    invalidateFrom('describing');
    setStep('describing');
  };

  const handleDescribeComplete = (current: string, updated: string) => {
    setSession((prev) => ({
      ...prev,
      currentDescription: current,
      aiUpdatedDescription: updated,
      editedDescription: updated,
    }));
    setStep('review');
  };

  const handleRegenerateDescription = () => {
    invalidateFrom('describing');
    setStep('describing');
  };

  const handleReviewNext = () => setStep('references');

  const handleReferencesGenerate = () => {
    invalidateFrom('generating');
    setStep('generating');
  };

  const handleGenerateComplete = (r2Key: string, imageUrl: string) => {
    setSession((prev) => ({
      ...prev,
      candidateR2Key: r2Key,
      candidateImageUrl: imageUrl,
    }));
    setStep('compare');
  };

  const handleAcceptRefined = async () => {
    if (!session.candidateR2Key) return;
    setAccepting(true);
    try {
      const resp = await api.post<AcceptRefinementResponse>(
        `/projects/${imageRef.project_id}/refine/accept-refinement`,
        { image_ref: imageRef, candidate_r2_key: session.candidateR2Key },
      );
      setSession((prev) => ({ ...prev, candidateR2Key: null, candidateImageUrl: null }));
      showToast('Refined image applied', 'success');
      onAccepted(resp);
      onClose();
    } catch (err) {
      toastError(showToast, err, 'Failed to apply the refinement');
    } finally {
      setAccepting(false);
    }
  };

  const handleKeepCurrent = async () => {
    if (!session.candidateR2Key) {
      onClose();
      return;
    }
    setRejecting(true);
    try {
      await api.post(
        `/projects/${imageRef.project_id}/refine/reject-refinement`,
        { image_ref: imageRef, candidate_r2_key: session.candidateR2Key },
      );
      setSession((prev) => ({ ...prev, candidateR2Key: null, candidateImageUrl: null }));
      showToast('Kept current image', 'info');
      onClose();
    } catch {
      onClose();
    } finally {
      setRejecting(false);
    }
  };

  const goBack = () => {
    if (step === 'describing') setStep('intent');
    else if (step === 'review') setStep('intent');
    else if (step === 'references') setStep('review');
    else if (step === 'generating') setStep('references');
    else if (step === 'compare') setStep('references');
  };

  return {
    step,
    session,
    setSession,
    accepting,
    rejecting,
    canProceedIntent,
    canProceedReview,
    handleClose,
    handleIntentNext,
    handleDescribeComplete,
    handleRegenerateDescription,
    handleReviewNext,
    handleReferencesGenerate,
    handleGenerateComplete,
    handleAcceptRefined,
    handleKeepCurrent,
    goBack,
  };
}
